import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';

import Config from '../../config';
import fetch from '../../utils/fetchService';

import { QueryKeys, useCustomQuery } from './index';
import { zipObject } from '../../scenarios/shared/utils';
import { handleErrors, parseAsJson } from '../../utils/requestHelpers';
import { YES_FLAG } from '../constants';
import { CurrencyCode, useCurrenciesList, useCurrenciesOptions, useTaxCurrencyDefaultCodes } from './currencies';
import { YesNoFlag } from '../types';
import { CountryTaxType, useCountryTaxTypesList } from './countryTaxTypes';
import {
  FederalFTCLimitOptions,
  NOLOptions,
  RecordVAOptions,
  SecondaryTaxOptions,
  StatePrePostNOLOptions,
} from '../../administration/obligationsPage/constants';
import {
  ReportName,
  ScenarioCompareReportData,
  ScenarioReportData,
} from '../../scenarios/scenarioReport/types';
import { assignmentTypes } from '../authorization/assignmentTypes';
import { getDetailKey } from '../../scenarios/scenarioReportWithDetails/RowDetails/utils';

export const useScenariosAllowedPeriods = () =>
  useCustomQuery(
    [QueryKeys.scenarios.AllowedPeriods],
    {
      url: Config.endpoints.FETCH_ALLOWED_PERIODS_FOR_SCENARIOS,
      errorMessage: 'Fetching allowed periods failed',
      defaultData: [],
    }
  );

export type ModificationMetadata = {
  added?: YesNoFlag,
  modified?: YesNoFlag,
  deleted?: YesNoFlag,
}

export type ScenarioMember = {
  companyCode: string,
  companyCodeDescription: string,
  periodName: string,
  scenarioEnabled: YesNoFlag,
  recordFTC: YesNoFlag,
  subledgerFlag: YesNoFlag,
  businessUnit: string,
  legalEntity: string,
  statCalc: YesNoFlag,
  functionalCurrency: string,
  overrideTaxCurrency: string,
  overrideSecondaryTax: 'Branch' | 'Shareholder' | '' | null,
  statusName?: 'Calculated' | 'Not Started' | '' | null,
} & ModificationMetadata

export enum NOL {
  UTILIZE = 'UTILIZE',
  RECORD = 'RECORD',
}

export enum RecordVA {
  ALL = 'ALL',
  NOL = 'NOL'
}

export enum PrePost {
  PRE = 'PRE',
  POST = 'POST',
}

export type ScenarioObligation = {
  periodName: string,
  companyCode: string,
  countryCode: string,
  countryTaxType: string,
  overrideTaxCurrency: string,
  federalFTCLimit: YesNoFlag | null,
  overrideTaxRate: number | null,
  overrideDefTaxRate: number | null,
  NOL: NOL | null,
  recordVA: RecordVA | null,
  stateApportionmentRate: number | null,
  stateMinimumTax: number | null,
  statePrePostNOL: PrePost | null
} & ModificationMetadata;

export enum ScenarioDataSources {
  TLS = 'TLS',
  Copy = 'COPY',
  Empty = 'EMPTY',
}

export type Scenario = {
  scenarioId: number,
  scenarioName: string,
  dataSource: ScenarioDataSources,
  periodName: string,
  enabledFlag: YesNoFlag,
  publicFlag: YesNoFlag,
  lockedFlag: YesNoFlag,
}

const YesNoOptions = ['Y', 'N'].map(value => ({ label: value, value }));

export const useScenariosList = () =>
  useCustomQuery<Scenario[]>(
    [QueryKeys.scenarios.ScenariosList],
    {
      url: Config.endpoints.FETCH_SCENARIOS,
      errorMessage: 'Fetching scenarios failed',
      defaultData: [],
    }
  );

type UseScenarioMetadataParams = {scenarioId: number, enabled?: true } | { scenarioId?: unknown, enabled: false };
export const useScenarioMetadata = ({ scenarioId, enabled }: UseScenarioMetadataParams) =>
  useCustomQuery<Scenario>(
    [QueryKeys.scenarios.ScenarioMetadata, { scenarioId }],
    {
      url: Config.endpoints.FETCH_SCENARIO_METADATA
        .replace(/:scenarioId/, String(scenarioId)),
      errorMessage: 'Fetching scenario metadata failed',
      enabled,
    }
  );

export const useScenarioReportsData = ({
  scenarioId,
  compareScenarioId,
  companyCodes,
  countryTaxTypes,
  reportingCurrency,
  reportsNames,
  columns = [],
}: {
  scenarioId: string,
  compareScenarioId?: number,
  companyCodes: string[],
  countryTaxTypes: string[],
  reportingCurrency: string,
  reportsNames: string[],
  columns: string[],
}) =>
  useCustomQuery<Record<ReportName, ScenarioReportData[]>>(
    [QueryKeys.scenarios.ScenarioReportsData, {
      scenarioId,
      compareScenarioId,
      companyCodes,
      countryTaxTypes,
      reportingCurrency,
      reportsNames,
      columns,
    }],
    {
      customFetch: async () => {
        const reportsData = await Promise.all(reportsNames.map(reportName =>
          fetch(
            Config.endpoints.FETCH_SCENARIO_CALC_DATA
              .replace(/:reportName/, reportName)
              .replace(/:scenarioId/, scenarioId),
            {
              method: 'POST',
              body: JSON.stringify({
                companyCodes,
                countryTaxTypes,
                columns,
                reportingCurrency,
              }),
            }
          )
            .then(handleErrors({ errorMessage: 'Fetching scenario calc data failed' }))
            .then(parseAsJson)
        ));

        return zipObject(reportsNames, reportsData);
      },
      defaultData: {},
      enabled: !compareScenarioId && Boolean(reportsNames.length),
    }
  );


export const useScenarioCompareReportsData = ({
  scenarioId,
  compareScenarioId,
  companyCodes,
  countryTaxTypes,
  reportingCurrency,
  reportsNames,
  columns = [],
}: {
  scenarioId: string,
  compareScenarioId?: number,
  companyCodes: string[],
  countryTaxTypes: string[],
  reportingCurrency: string,
  reportsNames: string[],
  columns: string[],
}) =>
  useCustomQuery<Record<ReportName, ScenarioCompareReportData[]>>(
    [QueryKeys.scenarios.ScenarioReportsCompareData, {
      scenarioId,
      compareScenarioId,
      companyCodes,
      countryTaxTypes,
      reportingCurrency,
      reportsNames,
      columns,
    }],
    {
      customFetch: async () => {
        const reportsData = await Promise.all(reportsNames.map(reportName =>
          fetch(
            Config.endpoints.FETCH_SCENARIO_COMPARE_CALC_DATA
              .replace(/:reportName/, reportName)
              .replace(/:scenarioId/, scenarioId)
              .replace(/:compareScenarioId/, `${compareScenarioId}`),
            {
              method: 'POST',
              body: JSON.stringify({
                companyCodes,
                countryTaxTypes,
                reportingCurrency,
                columns,
              }),
            }
          )
            .then(handleErrors({ errorMessage: 'Fetching scenario calc data failed' }))
            .then(parseAsJson)
        ));

        return zipObject(reportsNames, reportsData);
      },
      defaultData: {},
      enabled: Boolean(compareScenarioId) && Boolean(reportsNames.length),
    }
  );

export const useCombinedScenarioReportsData = ({
  scenarioId,
  compareScenarioId,
  companyCodes,
  countryTaxTypes,
  reportingCurrency,
  reportsNames,
  columns = [],
}: {
  scenarioId: string,
  compareScenarioId?: number,
  companyCodes: string[],
  countryTaxTypes: string[],
  reportingCurrency: string,
  reportsNames: string[],
  columns: string[],
}) => {
  const {
    data: scenarioReportData,
    isFetching: isFetchingScenariosReportData,
  } = useScenarioReportsData({
    scenarioId,
    companyCodes,
    countryTaxTypes,
    compareScenarioId,
    reportingCurrency,
    reportsNames,
    columns,
  });
  const {
    data: scenarioCompareReportData,
    isFetching: isFetchingScenariosCompareReportData,
  } = useScenarioCompareReportsData({
    scenarioId,
    companyCodes,
    countryTaxTypes,
    compareScenarioId,
    reportingCurrency,
    reportsNames,
    columns,
  });

  const queryClient = useQueryClient();
  const refetchReportsData = useCallback(() => queryClient.resetQueries({
    predicate: query => [
      QueryKeys.scenarios.ScenarioReportsData,
      QueryKeys.scenarios.ScenarioReportsCompareData,
    ].includes(query.queryKey[0] as string),
  }), [queryClient]);

  const reportsData = useMemo(() => (
    compareScenarioId ? scenarioCompareReportData : scenarioReportData
  ), [scenarioReportData, scenarioCompareReportData, compareScenarioId]);

  const isFetchingReportsData = isFetchingScenariosReportData || isFetchingScenariosCompareReportData;

  return [reportsData, isFetchingReportsData, refetchReportsData];
};

export type Params = {
  scenarioId: string | number,
  reportName: string,
  fieldName: string,
  companyCodes: string[],
  countryTaxTypes: string[],
  reportingCurrency?: string | null,
}

type Detail = {
  account: string,
  accountExceptionId: string,
  companyCode: string,
  countryTaxType: string,
  detailId: string,
  detailName: string,
  functionalCurrency: string,
  label?: string | null,
  name: string,
  overrideTaxCurrency?: string | null,
  scenarioId: number,
  touchedFlag: 'Y' | 'N',
  value: number,
}

const fetchDetails = async (data: Params[]) => Promise.all(data.map(async params => ({
  params,
  details: await fetch(
    Config.endpoints.FETCH_SCENARIO_DETAILS
      .replace(/:scenarioId/, String(params.scenarioId))
      .replace(/:reportName/, params.reportName)
      .replace(/:fieldName/, params.fieldName)
      .replace(/:companyCode/, params.companyCodes.join('&companyCode='))
      .replace(/:countryTaxType/, params.countryTaxTypes.join('&countryTaxType='))
      .replace(/:reportingCurrency/, params.reportingCurrency || '')
  ).then(parseAsJson) as Detail[],
})
));

type ScenarioId = string & { __typename: 'ScenarioId' };
type CompanyCode = string & { __typename?: 'CompanyCode' };
type CountryTaxTypeString = string & { __typename?: 'CountryTaxType' };

export type ScenarioRowKey = `${Detail['account']};${Detail['detailName']}${number | ''}`;
export type ScenarioDetailRowDataKey = `${ScenarioId};${CompanyCode};${CountryTaxTypeString}`;


export type ScenarioDetailRow = {
  rowKey?: ScenarioRowKey,
  account: string,
  detailName: string,
  details: Record<ScenarioDetailRowDataKey, {
    detailId: string,
    value: number,
    companyCode: string,
    countryTaxType: string,
    scenarioId: number,
    reportName: string,
    fieldName: string,
  }>
};


export const useScenarioReportRowDetails = ({ params = [], queryEnabled }: {
  params?: Params[],
  queryEnabled: boolean,
}) => {
  const { data, ...queryResult } = useCustomQuery(
    [QueryKeys.scenarios.ScenarioRowDetailsList, ...params],
    {
      customFetch: () => fetchDetails(params ?? []),
      enabled: queryEnabled,
      defaultData: [],
    }
  );

  const getKey = (detail: Detail) => `${detail.account};${detail.detailName}` as ScenarioRowKey;
  const parsedData = useMemo(() => Object.values(data?.reduce((acc, { params, details }) => {
    details.forEach(detail => {
      const { companyCode, countryTaxType, scenarioId } = detail;
      let rowKey = getKey(detail);
      let duplicatedDetails = 0;

      while (
        acc[rowKey]?.details[
          getDetailKey({ scenarioId, companyCode, countryTaxType }) as ScenarioDetailRowDataKey
        ]
      ) {
        rowKey = `${getKey(detail)}${++duplicatedDetails}` as ScenarioRowKey;
      }

      if (!acc[rowKey]) {
        acc[rowKey] = {
          rowKey,
          account: detail.account,
          detailName: detail.detailName,
          details: {},
        };
      }

      acc[rowKey].details[getDetailKey({ scenarioId, companyCode, countryTaxType }) as ScenarioDetailRowDataKey] = {
        value: detail.value,
        detailId: detail.detailId,
        companyCode,
        countryTaxType,
        scenarioId,
        reportName: params.reportName,
        fieldName: params.fieldName,
      };
    });

    return acc;
  }, {} as Record<string, ScenarioDetailRow>)), [data]);


  return {
    data: parsedData,
    ...queryResult,
  };
};


export const useScenarioDetails = ({
  scenarioId,
  reportName,
  fieldName,
  companyCode,
  countryTaxType,
  reportingCurrency,
  enabled,
}: {
  scenarioId: string,
  reportName: string,
  fieldName: string,
  companyCode: string,
  countryTaxType: string,
  reportingCurrency: string,
  enabled: boolean,
}) =>
  useCustomQuery(
    [QueryKeys.scenarios.ScenarioDetailsList, {
      scenarioId,
      reportName,
      fieldName,
      companyCode,
      countryTaxType,
      reportingCurrency,
    }],
    {
      url: Config.endpoints.FETCH_SCENARIO_DETAILS
        .replace(/:scenarioId/, scenarioId)
        .replace(/:reportName/, reportName)
        .replace(/:fieldName/, fieldName)
        .replace(/:companyCode/, companyCode)
        .replace(/:countryTaxType/, countryTaxType)
        .replace(/:reportingCurrency/, reportingCurrency),
      errorMessage: 'Fetching scenario details failed',
      defaultData: [],
      enabled: Boolean(scenarioId) && enabled,
    }
  );

export const useScenarioMembers = ({ scenarioId }: { scenarioId: string }) =>
  useCustomQuery(
    [QueryKeys.scenarios.ScenarioDetailsList, { scenarioId }],
    {
      url: Config.endpoints.FETCH_SCENARIO_COMPANY_CODES
        .replace(/:scenarioId/, scenarioId)
        .replace(/:scenarioEnabled/, YES_FLAG),
      errorMessage: 'Fetching scenario members failed',
      defaultData: [],
      enabled: Boolean(scenarioId),
    }
  );

export const useScenariosMembersList = ({
  scenarioId,
  scenarioEnabled = 'ALL',
}: {
  scenarioId: number,
  scenarioEnabled?: YesNoFlag | 'ALL'
}, { enabled = true }: { enabled?: boolean } = {}) =>
  useCustomQuery<ScenarioMember[]>([QueryKeys.scenariosMembers.MembersList, scenarioId, scenarioEnabled],
    {
      url: Config.endpoints.FETCH_SCENARIO_COMPANY_CODES
        .replace(/:scenarioId/, String(scenarioId))
        .replace(/:scenarioEnabled/, String(scenarioEnabled)),
      errorMessage: 'Fetching company codes failed',
      defaultData: [],
      enabled,
    });


type AllowedAssignment = {
  assignmentType: assignmentTypes,
  assignment: string,
  assignmentDescription: string,
}

type AllowedAssignments = Record<'businessUnits' | 'connections' | 'countries' | 'businesses', AllowedAssignment[]>

export const useScenarioAllowedAssignments = ({ periodName }: { periodName?: string }) =>
  useCustomQuery<AllowedAssignments>([QueryKeys.scenarios.ScenarioAllowedAssignments], {
    url: Config.endpoints.FETCH_SCENARIO_ALLOWED_ASSIGNMENTS
      .replace(/:periodName/, periodName || ''),
    errorMessage: 'Error fetching allowed assignments',
    enabled: !!periodName,
    defaultData: {
      countries: [],
      businesses: [],
      businessUnits: [],
      connections: [],
    },
  });

export const useScenarioAllowedAssignmentOptions = ({ periodName }: { periodName?: string }) => {
  const { data: allowedAssignments, ...queryApi } = useScenarioAllowedAssignments({ periodName });

  const options = useMemo(() => mapValues(
    allowedAssignments,
    (assignments: AllowedAssignment[]) => assignments.map(assignment => ({
      label: assignment.assignmentDescription,
      value: assignment.assignment,
    }))),
  [allowedAssignments]
  );

  return {
    data: options,
    ...queryApi,
  };
};

export const useScenariosMembersListLookupData = ({ periodName }: { periodName?: string }) => {
  const {
    data: currencies,
    isLoading: isLoadingCurrencies,
  } = useCurrenciesList();

  const {
    data: options,
    isLoading: isLoadingScenarioAllowedAssignmentOptions,
  } = useScenarioAllowedAssignmentOptions({ periodName });

  return {
    isLoading: isLoadingCurrencies || isLoadingScenarioAllowedAssignmentOptions,
    lookUpData: {
      currencyOptions: (currencies as CurrencyCode[]).map(({ currencyCode }) => ({
        label: currencyCode,
        value: currencyCode,
      })),
      countriesOptions: options.countries,
      businessUnitOptions: options.businessUnits,
      businessesOptions: options.businesses,
      connectionsOptions: options.connections,
      countriesByCode: keyBy(options.countries.map(({ value, label }) => ({
        countryCode: value,
        countryDescription: label,
      })), 'countryCode'),
      yesNoOptions: YesNoOptions,
      secondaryTaxOptions: SecondaryTaxOptions,
    },
  };
};

export type ScenariosMembersListLookupData = (ReturnType<typeof useScenariosMembersListLookupData>)['lookUpData'];

export const useScenariosObligationsList = ({ scenarioId }: { scenarioId: number }) =>
  useCustomQuery<ScenarioObligation[]>([QueryKeys.scenariosObligations.ObligationsList, { scenarioId }], {
    url: Config.endpoints.FETCH_SCENARIO_OBLIGATIONS
      .replace(/:scenarioId/, String(scenarioId)),
    errorMessage: 'Fetching obligations failed',
    defaultData: [],
  });

type EncodedCountryTaxType = string;

const encodeCtt = <T extends Pick<CountryTaxType, 'countryCode'|'countryTaxType'>>({ countryCode, countryTaxType }: T): EncodedCountryTaxType => `${countryCode}-${countryTaxType}`;

export const useScenariosObligationsListLookupData = ({
  scenarioId,
}: {
  scenarioId: number,
}) => {
  const {
    data: { periodName } = {},
    isLoading: isLoadingScenarioMetadata,
  } = useScenarioMetadata({ scenarioId });


  const {
    data: currenciesOptions,
    isLoading: isLoadingCurrenciesOptions,
  } = useCurrenciesOptions();

  const {
    data: countryTaxTypes,
    isLoading: isLoadingCountryTaxTypesOptions,
    isIdle: isIdleCountryTaxTypesOptions,
  } = useCountryTaxTypesList({ periodName: periodName ?? '' }, { enabled: Boolean(periodName) });

  const {
    data: companyCodes,
    isLoading: isLoadingCompanyCodes,
  } = useScenariosMembersList({ scenarioId });

  const {
    data: taxCurrencyDefaultCodes,
    isLoading: isLoadingTaxCurrencyDefaultCodes,
  } = useTaxCurrencyDefaultCodes();

  return {
    isLoading: isLoadingCurrenciesOptions
      || isLoadingScenarioMetadata
      || isLoadingCompanyCodes
      || isLoadingCountryTaxTypesOptions
      || isIdleCountryTaxTypesOptions
      || isLoadingTaxCurrencyDefaultCodes,
    lookUpData: {
      countryTaxTypeData: keyBy(countryTaxTypes, encodeCtt),
      countryTaxTypesOptions: countryTaxTypes!.map(ctt => ({
        value: encodeCtt(ctt),
        label: `${ctt.countryTaxType} - ${ctt.description}`,
      })),
      currenciesOptions,
      companyCodeOptions: companyCodes!.map(({ companyCode, companyCodeDescription, subledgerFlag }) => ({
        label: `${companyCode} - ${companyCodeDescription}`,
        value: companyCode,
        subledgerFlag,
      })),
      taxCurrencyDefaultCodes,
      YesNoOptions,
      federalFTCLimitOptions: FederalFTCLimitOptions,
      NOLOptions,
      recordVaOptions: RecordVAOptions,
      statePrePostNOLOptions: StatePrePostNOLOptions,
      periodName,
    },
  };
};

export type ScenariosObligationsListLookupData = (ReturnType<typeof useScenariosObligationsListLookupData>)['lookUpData'];

export const useScenarioDetailsForExport = ({
  scenarioId,
  compareScenarioId,
  companyCodes,
  reportingCurrency,
  reportParams,
}: {
  scenarioId: string,
  compareScenarioId?: number,
  companyCodes: string[],
  reportingCurrency: string,
  reportParams: { reportName: string, countryTaxTypes: string[] }[],
  isComparing: boolean,
}) =>
  useCustomQuery(
    [QueryKeys.scenarios.ScenarioDetailsForExport, {
      scenarioId,
      compareScenarioId,
      companyCodes,
      reportingCurrency,
      reportParams,
    }],
    {
      customFetch: async () => {
        let comparedResults = [];
        const results = await Promise.all(reportParams.map(({ reportName, countryTaxTypes }) =>
          fetch(
            Config.endpoints.FETCH_SCENARIO_DETAILS_FOR_REPORT_EXPORT
              .replace(/:scenarioId/, scenarioId),
            {
              method: 'POST',
              body: JSON.stringify({
                reportName,
                companyCodes,
                countryTaxTypes,
                reportingCurrency,
              }),
            }
          )
            .then(handleErrors({ errorMessage: 'Fetching scenario details for export failed' }))
            .then(parseAsJson)
        ));
        if (compareScenarioId) {
          comparedResults = await Promise.all(reportParams.map(({ reportName, countryTaxTypes }) =>
            fetch(
              Config.endpoints.FETCH_SCENARIO_DETAILS_FOR_REPORT_EXPORT
                .replace(/:scenarioId/, `${compareScenarioId}`),
              {
                method: 'POST',
                body: JSON.stringify({
                  reportName,
                  companyCodes,
                  countryTaxTypes,
                  reportingCurrency,
                }),
              }
            )
              .then(handleErrors({ errorMessage: 'Fetching scenario details for export failed' }))
              .then(parseAsJson)
          ));
        }

        const baseDetailsData = zipObject(reportParams.map(({ reportName }) => reportName), results);
        const compareDetailsData = compareScenarioId
          ? zipObject(reportParams.map(({ reportName }) => reportName), comparedResults)
          : null;

        return { baseDetailsData, compareDetailsData };
      },
      defaultData: { baseDetailsData: [], compareDetailsData: [] },
      enabled: false,
    }
  );
